import { IconAlertsDanger } from '@doltech/icons/IconAlertsDanger';
import { IconAlertsInfo } from '@doltech/icons/IconAlertsInfo';
import { IconArrowsCheckO } from '@doltech/icons/IconArrowsCheckO';
import { IconButtonsCloseO } from '@doltech/icons/IconButtonsCloseO';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Modal } from '@doltech/ui/lib/figma/DesignSystemV2/Modal/Modal';
import { Container } from '@doltech/ui/lib/figma/Modal/ConfirmModal';
import { ReadingText } from '@doltech/ui/lib/figma/Typography';
import * as React from 'react';

import { BeforeSubmitTestData } from '../event/BeforeSubmitTestEventHandler';
import { ActionOnFieldHandler } from './ActionOnFieldHandler';

const ModalType = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  SUCCESS: 'success',
} as const;

type ModalType = (typeof ModalType)[keyof typeof ModalType];

export interface ShowModalAction {
  title: string;
  message: string;
  type: ModalType;
  placeholder: string[];
  icon: string;
  confirm: {
    label: string;
    action: any;
  };
  cancel: {
    label: string;
    action: any;
  };
}

export class ShowModalActionHandler extends ActionOnFieldHandler {
  constructor() {
    super('showPopUp');
  }

  getAction(action: any): ShowModalAction {
    return super.getAction(action) as ShowModalAction;
  }

  handleAction(
    actionPolicy: ShowModalAction,
    data: BeforeSubmitTestData,
    subActionHandle
  ): Promise<void> {
    if (this.canHandleAction(actionPolicy)) {
      const showModelAction = this.getAction(actionPolicy);
      let modalAction;
      switch (showModelAction.type) {
        case ModalType.INFO: {
          modalAction = Modal.Info.create();
          break;
        }
        case ModalType.ERROR: {
          modalAction = Modal.Error.create();
          break;
        }
        case ModalType.WARNING: {
          modalAction = Modal.Warning.create();
          break;
        }
        case ModalType.SUCCESS: {
          modalAction = Modal.Success.create();
          break;
        }
        default:
          modalAction = Modal.Basic.create();
          break;
      }
      const { modal, render } = modalAction;

      return new Promise<void>((resolve, reject) => {
        render(
          () => {
            return (
              <Container>
                <ReadingText block>
                  {this.placeholderBuilder(showModelAction, showModelAction.message, data)}
                </ReadingText>
              </Container>
            );
          },
          {
            title: showModelAction.title,
            okText: showModelAction.confirm?.label,
            icon: iconBuilder[showModelAction.icon],
            cancelText: showModelAction.cancel?.label,
            closable: false,
            maskClosable: false,
            centered: true,
            onOk: async () => {
              await subActionHandle(showModelAction.confirm?.action);
              modal.destroy();
              resolve();
            },
            onCancel: async () => {
              await subActionHandle(showModelAction.cancel?.action);
              modal.destroy();
              resolve();
            },
          }
        );
      });
    }
    return Promise.resolve();
  }
}

const iconBuilder = {
  info: <IconAlertsInfo fontSize={32} color={colorsV2.blue100} />,
  warning: <IconAlertsDanger fontSize={32} color={colorsV2.yellow100} />,
  error: <IconButtonsCloseO fontSize={32} color={colorsV2.primary100} />,
  success: <IconArrowsCheckO fontSize={32} color={colorsV2.green100} />,
};
